import Papa from 'papaparse';
import { z } from 'zod';
import { normalizePhone, DEFAULT_COUNTRY } from './phone/normalizePhone';

export const CSV_TEMPLATE_HEADERS = ['nombre', 'pases', 'whatsapp', 'grupo', 'rotulo'] as const;

export const CSV_TEMPLATE = `nombre,pases,whatsapp,grupo,rotulo
Juan Pérez,2,59171234567,Familia novia,Juan Pérez y esposa
María García,1,59177654321,Amigas,María
`;

const GROUP_ALIASES: Record<string, string> = {
  'familia novia': 'familia_novia',
  'familia de la novia': 'familia_novia',
  'familia novio': 'familia_novio',
  'familia del novio': 'familia_novio',
  'amigos novia': 'amigos_novia',
  'amigas novia': 'amigos_novia',
  'amigas': 'amigos_novia',
  'amigos novio': 'amigos_novio',
  'amigos': 'amigos_novio',
  'trabajo': 'trabajo',
  'otros': 'otros',
};

function stripAccents(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// 008 · i18n (T072) — delega en el helper único `normalizePhone`. País configurable; por
// defecto Bolivia (MVP). El comportamiento +591/8 dígitos es idéntico al previo.
export function normalizeWhatsapp(raw: string, country: string = DEFAULT_COUNTRY): string | null {
  return normalizePhone(raw, country);
}

export function normalizeGroup(raw: string | undefined | null): string {
  if (!raw) return 'otros';
  const key = stripAccents(raw.trim().toLowerCase());
  if (GROUP_ALIASES[key]) return GROUP_ALIASES[key];
  return raw.trim().slice(0, 50);
}

export const csvRowSchema = z.object({
  nombre: z
    .string({ required_error: 'Nombre es requerido' })
    .transform((s) => (s ?? '').trim())
    .pipe(z.string().min(2, 'Nombre debe tener al menos 2 caracteres')),
  pases: z
    .union([z.string(), z.number()])
    .transform((v, ctx) => {
      const n = typeof v === 'number' ? v : parseInt((v ?? '').toString().trim(), 10);
      if (!Number.isFinite(n) || !Number.isInteger(n)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Pases debe ser un número entero' });
        return z.NEVER;
      }
      if (n < 1 || n > 10) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Pases debe estar entre 1 y 10' });
        return z.NEVER;
      }
      return n;
    }),
  whatsapp: z
    .string({ required_error: 'WhatsApp es requerido' })
    .transform((s, ctx) => {
      const normalized = normalizeWhatsapp(s);
      if (!normalized) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'WhatsApp inválido (formato Bolivia: +591 + 8 dígitos)',
        });
        return z.NEVER;
      }
      return normalized;
    }),
  grupo: z
    .string()
    .max(50, 'Grupo debe ser máximo 50 caracteres')
    .optional()
    .nullable()
    .transform((v) => normalizeGroup(v ?? undefined)),
  // 008 · US4 — rótulo de invitación opcional; si va vacío, se usa el nombre.
  rotulo: z
    .string()
    .max(120, 'Rótulo debe ser máximo 120 caracteres')
    .optional()
    .nullable()
    .transform((v) => (v ?? '').trim() || undefined),
});

export type CSVRowInput = {
  nombre?: string;
  pases?: string | number;
  whatsapp?: string;
  grupo?: string;
  rotulo?: string;
};

export type CSVRowValid = z.infer<typeof csvRowSchema>;

export interface CSVInvalidRow {
  rowNumber: number;
  row: CSVRowInput;
  errors: string[];
}

export interface CSVParseResult {
  valid: CSVRowValid[];
  invalid: CSVInvalidRow[];
}

function buildResult(rows: CSVRowInput[]): CSVParseResult {
  const valid: CSVRowValid[] = [];
  const invalid: CSVInvalidRow[] = [];
  rows.forEach((row, idx) => {
    const parsed = csvRowSchema.safeParse(row);
    if (parsed.success) {
      valid.push(parsed.data);
    } else {
      invalid.push({
        rowNumber: idx + 2,
        row,
        errors: parsed.error.issues.map((i) => i.message),
      });
    }
  });
  return { valid, invalid };
}

const PAPA_CONFIG = {
  header: true,
  skipEmptyLines: true,
  transformHeader: (h: string) => h.trim().toLowerCase(),
} as const;

export function parseCSVString(text: string): CSVParseResult {
  const result = Papa.parse<CSVRowInput>(text, PAPA_CONFIG);
  return buildResult(result.data ?? []);
}

export function parseCSVFile(file: File): Promise<CSVParseResult> {
  return new Promise((resolve, reject) => {
    Papa.parse<CSVRowInput>(file, {
      ...PAPA_CONFIG,
      complete: (results) => resolve(buildResult(results.data ?? [])),
      error: (err) => reject(err),
    });
  });
}
